import React from "react";
import { RfxListPage } from "../RfxListPage";
import { getRfxListSurface } from "../surfaces/rfx-list-surface";
import { TendersQuotesList } from "../components/TendersQuotes/TendersQuotesList";
import { TendersQuotesEmptyState } from "../components/TendersQuotes/TendersQuotesEmptyState";
import { TendersQuotesHeaderActions } from "../components/TendersQuotes/TendersQuotesHeaderActions";
import { TENDERS_QUOTES_TABS } from "../components/TendersQuotes/tendersQuotesTabAssertion";
import type { RfxListTab } from "../types/rfx-list.type";
import type { Rfx } from "../types/rfx.types";

interface RfxTendersQuotesPageProps {
	companyId?: string;
	path?: string;
	tab?: string;
}

const DEFAULT_TAB: RfxListTab = "current";

const resolveTab = (tab: string | undefined): RfxListTab =>
	(TENDERS_QUOTES_TABS as readonly string[]).includes(tab ?? "")
		? (tab as RfxListTab)
		: DEFAULT_TAB;

/**
 * Surface descriptor for this list — tabs, title, hrefs, default tab and data
 * source. Module-level because it is pure data: `useList` is a hook, but it is
 * only *called* during the shell's render, never here.
 */
const surface = getRfxListSurface<Rfx>("tenders_quotes");

/**
 * Route mount for the Tenders & Quotes scope — procurer feature-parity
 * rebuild of legacy `src/pages/RFQ/RFQ/RFQ.js` (TASK-008). Consumes 007's
 * `RfxListPage` template/`useRfxList` hook. Opts into 017's search/sort
 * controls (`searchable`) — Approvals/Panel leave it off by default.
 */
export const RfxTendersQuotesPage = ({
	companyId,
	tab,
}: RfxTendersQuotesPageProps) => {
	const resolvedTab = resolveTab(tab);

	return (
		<RfxListPage<Rfx>
			surface={surface}
			companyId={companyId}
			tab={tab}
			headerActions={
				companyId ? (
					<TendersQuotesHeaderActions companyId={companyId} />
				) : null
			}
			renderList={(rows, meta, view) => (
				<TendersQuotesList
					rows={rows}
					meta={meta}
					currentTab={resolvedTab}
					view={view}
				/>
			)}
			renderEmptyState={() => (
				<TendersQuotesEmptyState tab={resolvedTab} />
			)}
		/>
	);
};
